/** A random source in [0, 1): `Math.random` at runtime, a seeded one in tests and fixtures. */
export type Rng = () => number;

/** mulberry32: a small, fast seeded generator (deterministic sequences for the tests and the RTP run). */
export function seededRng(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** An integer in [min, max]. */
export function randomInt(rng: Rng, min: number, max: number): number {
  return min + Math.floor(rng() * (max - min + 1));
}

/** One entry of `table`, picked by weight. */
export function pickWeighted<T>(rng: Rng, table: readonly { value: T; weight: number }[]): T {
  const total = table.reduce((sum, entry) => sum + entry.weight, 0);
  let roll = rng() * total;
  for (const entry of table) {
    roll -= entry.weight;
    if (roll < 0) return entry.value;
  }
  return table[table.length - 1]!.value;
}

/** `items` in a random order (Fisher–Yates over a copy). */
export function shuffled<T>(rng: Rng, items: readonly T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}
